// src/components/admin/SizeManager.tsx
import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Grid,
  Card,
  CardContent,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
} from '@mui/icons-material';
import { 
  useGetProductSizesQuery, 
  useAddProductSizeMutation, 
  useUpdateProductSizeMutation, 
  useDeleteProductSizeMutation 
} from '../../features/admin/productApi.ts';

interface SizeManagerProps {
  productId: number;
}

const SizeManager: React.FC<SizeManagerProps> = ({ productId }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSize, setEditingSize] = useState<any>(null);
  const [sizeForm, setSizeForm] = useState({
    size: '',
    price: 0,
    retail_price: 0,
    discount_type: 0,
    discount: 0,
  });

  const { data: sizesResponse, refetch } = useGetProductSizesQuery({ product_id: productId });
  const [addSize] = useAddProductSizeMutation();
  const [updateSize] = useUpdateProductSizeMutation();
  const [deleteSize] = useDeleteProductSizeMutation();

  const sizes = sizesResponse?.data || [];

  const handleAddSize = () => {
    setSizeForm({
      size: '',
      price: 0,
      retail_price: 0,
      discount_type: 0,
      discount: 0,
    });
    setEditingSize(null);
    setDialogOpen(true);
  };

  const handleEditSize = (size: any) => {
    setSizeForm({
      size: size.size,
      price: size.price,
      retail_price: size.retail_price,
      discount_type: size.discount_type || 0,
      discount: size.discount || 0,
    });
    setEditingSize(size);
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const sizeData = {
        product_id: productId,
        ...sizeForm,
        ...(editingSize && { id: editingSize.id }),
      };
      
      if (editingSize) {
        await updateSize(sizeData).unwrap();
      } else {
        await addSize(sizeData).unwrap();
      }
      
      setDialogOpen(false);
      refetch();
    } catch (error) {
      console.error('Error saving size:', error);
    }
  };

  const handleDeleteSize = async (sizeId: number) => {
    if (window.confirm('Are you sure you want to delete this size?')) {
      try {
        await deleteSize({ product_id: productId, id: sizeId }).unwrap();
        refetch();
      } catch (error) {
        console.error('Error deleting size:', error);
      }
    }
  };

  // Predefined size options
  const sizeOptions = [
    'XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL',
    '28', '30', '32', '34', '36', '38', '40', '42', '44', '46',
    'Small', 'Medium', 'Large', 'Extra Large',
    'One Size', 'Custom'
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Size Variants</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAddSize}
        >
          Add Size
        </Button>
      </Box>

      <Grid container spacing={2}>
        {sizes.map((size) => (
          <Grid item xs={12} sm={6} md={4} key={size.id}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Chip
                    label={size.size}
                    color="primary"
                    variant="outlined"
                  />
                </Box>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    Price: ${size.price}
                  </Typography>
                  <Typography variant="body2">
                    Retail: ${size.retail_price}
                  </Typography>
                  {size.discount > 0 && (
                    <Typography variant="body2" color="error">
                      Discount: {size.discount_type === 1 ? `${size.discount}%` : `$${size.discount}`}
                    </Typography>
                  )}
                </Box>
                
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <IconButton
                    size="small"
                    onClick={() => handleEditSize(size)}
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteSize(size.id)}
                  >
                    <Delete />
                  </IconButton>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
        
        {sizes.length === 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">
                No size variants added yet
              </Typography>
            </Paper>
          </Grid>
        )}
      </Grid>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingSize ? 'Edit Size' : 'Add Size'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Size"
              value={sizeForm.size}
              onChange={(e) => setSizeForm({ ...sizeForm, size: e.target.value })}
              fullWidth
              select
              SelectProps={{
                native: true,
              }}
            >
              <option value="">Select a size</option>
              {sizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </TextField>
            
            <TextField
              label="Price"
              type="number"
              value={sizeForm.price}
              onChange={(e) => setSizeForm({ ...sizeForm, price: Number(e.target.value) })}
              fullWidth
            />
            
            <TextField
              label="Retail Price"
              type="number"
              value={sizeForm.retail_price}
              onChange={(e) => setSizeForm({ ...sizeForm, retail_price: Number(e.target.value) })}
              fullWidth
            />
            
            <FormControl fullWidth>
              <InputLabel>Discount Type</InputLabel>
              <Select
                value={sizeForm.discount_type}
                onChange={(e) => setSizeForm({ ...sizeForm, discount_type: Number(e.target.value) })}
                label="Discount Type"
              >
                <MenuItem value={0}>No Discount</MenuItem>
                <MenuItem value={1}>Percentage</MenuItem>
                <MenuItem value={2}>Fixed Amount</MenuItem>
              </Select>
            </FormControl>
            
            {sizeForm.discount_type > 0 && (
              <TextField
                label="Discount Value"
                type="number"
                value={sizeForm.discount}
                onChange={(e) => setSizeForm({ ...sizeForm, discount: Number(e.target.value) })}
                fullWidth
                InputProps={{
                  endAdornment: sizeForm.discount_type === 1 ? '%' : '$',
                }}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingSize ? 'Update' : 'Add'} Size
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SizeManager;